//AdminPage.js
//admin page for managing column configurations
import React, { useState, useEffect, useCallback } from 'react';
import { ColumnForm } from '../components/forms';
import GlobalUnitsManager from '../components/GlobalUnitsManager';
import LookupFieldDemo from '../components/forms/LookupFieldDemo';
import { apiFetch } from '../api/api'; // Assuming apiFetch is set up
import './AdminConfiguration.css';
// Import shared format options
import {
  COLUMN_TYPES,
  FIELD_INPUT_OPTIONS,
  mapDbTypeToColumnType,
  mapColumnTypeToFormatType,
  normalizeFormatValue
} from '../utils/formatters';

// Modal component (basic inline for simplicity, consider moving to a separate file)
const Modal = ({ isOpen, onClose, title, children, width = '400px' }) => {
  if (!isOpen) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', 
      alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }} onClick={onClose}> {/* Close on overlay click */}
      <div style={{ 
          backgroundColor: 'white', padding: '20px', borderRadius: '5px', 
          minWidth: width, maxWidth: '90%', maxHeight: '90vh', overflowY: 'auto' 
        }} 
        onClick={e => e.stopPropagation()} /* Prevent closing when clicking inside modal */
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{margin: 0}}>{title}</h2>
          <button onClick={onClose} style={{ fontSize: '1.5em', background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
};

// Helper function for label generation
const generateLabelFromName = (name) => {
  if (!name) return '';
  let label = name.replace(/_/g, ' '); // Replace underscores with spaces
  // Add space before capital letters (e.g., SpudDate -> Spud Date, WellAPI -> Well API)
  label = label.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2'); // Handles cases like APIValue
  label = label.replace(/([a-z\d])([A-Z])/g, '$1 $2');   // Handles cases like wellName or item1Name
  // Capitalize first letter of each word
  // return label.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  // Per request: SpudDate -> Spud Date (not Spud date), so direct space insertion is fine.
  // If the first letter needs to be capitalized and others not necessarily:
  // label = label.charAt(0).toUpperCase() + label.slice(1);
  return label.trim();
};

const AdminPage = () => {
  const [allTableConfigs, setAllTableConfigs] = useState([]);
  const [selectedTableIndex, setSelectedTableIndex] = useState(null);
  const [sqlCommand, setSqlCommand] = useState('');
  const [editingColumn, setEditingColumn] = useState(null);
  const [isEditColumnModalOpen, setIsEditColumnModalOpen] = useState(false);
  const [newTableNameInput, setNewTableNameInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState({ type: '', text: '' });

  // State for DB column discovery
  const [isDiscoverModalOpen, setIsDiscoverModalOpen] = useState(false);
  const [discoveredDbColumns, setDiscoveredDbColumns] = useState([]);
  const [selectedDbColumnsToAdd, setSelectedDbColumnsToAdd] = useState(new Set());
  const [isLoadingDbColumns, setIsLoadingDbColumns] = useState(false);
  const [dbSchemaName, setDbSchemaName] = useState('dbo'); // Default schema

  // NEW: State for dynamic database table discovery
  const [availableDbTables, setAvailableDbTables] = useState([]);
  const [isLoadingDbTables, setIsLoadingDbTables] = useState(false);
  const [columnValidationResults, setColumnValidationResults] = useState({});
  const [showValidationPanel, setShowValidationPanel] = useState(false);

  // Drag and drop state
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showGlobalUnitsManager, setShowGlobalUnitsManager] = useState(false);
  const [showLookupDemo, setShowLookupDemo] = useState(false);

  const userRole = 'admin'; // Mock user role

  // CSS Templates for table styling
  const tableCssTemplates = {
    default: {
      name: 'Default Table',
      css: `
table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
th {
  background: #f8fafc;
  color: #374151;
  font-weight: 600;
  padding: 12px 16px;
  text-align: left;
  border-bottom: 2px solid #e5e7eb;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
td {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #111827;
}
tr:hover {
  background: #f8fafc;
}
tr:last-child td {
  border-bottom: none;
}`
    },
    striped: {
      name: 'Striped Table',
      css: `
table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
th {
  background: #3b82f6;
  color: #ffffff;
  font-weight: 600;
  padding: 16px;
  text-align: left;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
td {
  padding: 14px 16px;
  color: #374151;
}
tr:nth-child(even) {
  background: #f8fafc;
}
tr:nth-child(odd) {
  background: #ffffff;
}
tr:hover {
  background: #e0f2fe !important;
}`
    },
    modern: {
      name: 'Modern Table',
      css: `
table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin: 16px 0;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}
th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  font-weight: 700;
  padding: 20px;
  text-align: left;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
td {
  padding: 18px 20px;
  color: #374151;
  border-bottom: 1px solid #f1f5f9;
}
tr:hover {
  background: linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%);
}`
    },
    minimal: {
      name: 'Minimal Table',
      css: `
table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  background: transparent;
}
th {
  background: transparent;
  color: #1f2937;
  font-weight: 700;
  padding: 8px 12px;
  text-align: left;
  border-bottom: 2px solid #1f2937;
  font-size: 0.875rem;
}
td {
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  color: #374151;
}
tr:hover {
  background: #f9fafb;
}`
    },
    colorful: {
      name: 'Colorful Table',
      css: `
table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
th {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%);
  color: #78350f;
  font-weight: 700;
  padding: 16px;
  text-align: left;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
td {
  padding: 14px 16px;
  color: #374151;
  border-bottom: 1px solid #fef3c7;
}
tr:nth-child(even) {
  background: #fffbeb;
}
tr:hover {
  background: #fef3c7 !important;
}`
    }
  };

  // Table title CSS templates
  const titleCssTemplates = {
    default: {
      name: 'Default Title',
      css: `
.table-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
}`
    },
    modern: {
      name: 'Modern Title',
      css: `
.table-title {
  font-size: 1.75rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 20px;
  padding: 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}`
    },
    colorful: {
      name: 'Colorful Title',
      css: `
.table-title {
  font-size: 1.625rem;
  font-weight: 700;
  color: #92400e;
  background: linear-gradient(135deg, #fef3c7 0%, #fcd34d 100%);
  padding: 12px 20px;
  border-radius: 8px;
  margin-bottom: 16px;
  text-shadow: 0 1px 2px rgba(146, 64, 14, 0.1);
  border-left: 4px solid #f59e0b;
}`
    },
    minimal: {
      name: 'Minimal Title',
      css: `
.table-title {
  font-size: 1.25rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 12px;
  padding: 0;
  text-transform: lowercase;
  font-family: 'Courier New', monospace;
}`
    }
  };

  // Debug effect to log table configs changes
  useEffect(() => {
    console.log('allTableConfigs updated:', allTableConfigs);
    console.log('selectedTableIndex:', selectedTableIndex);
  }, [allTableConfigs, selectedTableIndex]);

  const fetchConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await apiFetch('/api/config/columns');
      setAllTableConfigs(Array.isArray(data) ? data : []);
      if (Array.isArray(data) && data.length > 0 && selectedTableIndex === null) {
        setSelectedTableIndex(0);
      } else if (!Array.isArray(data) || data.length === 0){
        setSelectedTableIndex(null);
      }
      // Feedback on initial load can be omitted or made less intrusive
    } catch (error) {
      console.error("Failed to fetch config:", error);
      setAllTableConfigs([]);
      setSelectedTableIndex(null);
      setFeedbackMessage({ type: 'error', text: `Failed to load configuration: ${error.message}` });
    }
    setIsLoading(false);
  }, []); // Removed selectedTableIndex dependency

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Auto-fetch database tables when component loads
  useEffect(() => {
    if (allTableConfigs.length > 0) {
      fetchAvailableDbTables();
    }
  }, [allTableConfigs.length]); // Only run when configs are loaded

  const getCurrentTableConfig = useCallback(() => {
    if (selectedTableIndex !== null && allTableConfigs[selectedTableIndex]) {
      return allTableConfigs[selectedTableIndex];
    }
    return null;
  }, [allTableConfigs, selectedTableIndex]);

  const currentTableConfig = getCurrentTableConfig();
  const columns = currentTableConfig ? currentTableConfig.columns : [];
  const tableName = currentTableConfig ? currentTableConfig.tableName : '';

  const updateCurrentTableConfigInState = (updatedTableColumns) => {
    if (selectedTableIndex === null) return allTableConfigs; 
    const newAllTableConfigs = [...allTableConfigs];
    newAllTableConfigs[selectedTableIndex] = {
      ...newAllTableConfigs[selectedTableIndex],
      columns: updatedTableColumns,
    };
    setAllTableConfigs(newAllTableConfigs);
    return newAllTableConfigs;
  };

  // Apply table CSS template
  const applyTableCSSTemplate = (templateKey) => {
    if (selectedTableIndex === null) return;
    const template = tableCssTemplates[templateKey];
    if (!template) return;

    const newAllTableConfigs = [...allTableConfigs];
    newAllTableConfigs[selectedTableIndex] = {
      ...newAllTableConfigs[selectedTableIndex],
      tableCSS: template.css
    };
    setAllTableConfigs(newAllTableConfigs);
    setFeedbackMessage({ type: 'info', text: `Table CSS template "${template.name}" applied successfully.` });
  };

  // Apply title CSS template
  const applyTitleCSSTemplate = (templateKey) => {
    if (selectedTableIndex === null) return;
    const template = titleCssTemplates[templateKey];
    if (!template) return;

    const newAllTableConfigs = [...allTableConfigs];
    newAllTableConfigs[selectedTableIndex] = {
      ...newAllTableConfigs[selectedTableIndex],
      titleCSS: template.css
    };
    setAllTableConfigs(newAllTableConfigs);
    setFeedbackMessage({ type: 'info', text: `Title CSS template "${template.name}" applied successfully.` });
  };

  // Update custom table CSS
  const updateTableCSS = (customCSS) => {
    if (selectedTableIndex === null) return;
    
    const newAllTableConfigs = [...allTableConfigs];
    newAllTableConfigs[selectedTableIndex] = {
      ...newAllTableConfigs[selectedTableIndex],
      tableCSS: customCSS
    };
    setAllTableConfigs(newAllTableConfigs);
  };

  // Update custom title CSS
  const updateTitleCSS = (customCSS) => {
    if (selectedTableIndex === null) return;
    
    const newAllTableConfigs = [...allTableConfigs];
    newAllTableConfigs[selectedTableIndex] = {
      ...newAllTableConfigs[selectedTableIndex],
      titleCSS: customCSS
    };
    setAllTableConfigs(newAllTableConfigs);
  };

  // Update table display name
  const updateTableDisplayName = (displayName) => {
    if (selectedTableIndex === null) return;
    
    const newAllTableConfigs = [...allTableConfigs];
    newAllTableConfigs[selectedTableIndex] = {
      ...newAllTableConfigs[selectedTableIndex],
      displayName: displayName
    };
    setAllTableConfigs(newAllTableConfigs);
  };

  // Update AI instructions
  const updateAIInstructions = (aiInstructions) => {
    if (selectedTableIndex === null) return;
    
    const newAllTableConfigs = [...allTableConfigs];
    newAllTableConfigs[selectedTableIndex] = {
      ...newAllTableConfigs[selectedTableIndex],
      aiInstructions: aiInstructions
    };
    setAllTableConfigs(newAllTableConfigs);
  };

  const handleAddColumn = (newColumnData) => { // From the main "Add New Column" form
    if (!currentTableConfig) return;
    const newOrder = columns.length > 0 ? Math.max(...columns.map(c => c.order), 0) + 1 : 1;
    const finalNewColumn = { ...newColumnData, order: newOrder };
    const updatedColumns = [...columns, finalNewColumn];
    updateCurrentTableConfigInState(updatedColumns);
    generateAddColumnSql(finalNewColumn);
    setFeedbackMessage({ type: 'info', text: `Column '${finalNewColumn.name}' staged for addition. Save configuration to persist.` });
  };

  const handleUpdateColumn = (updatedColumnData) => { // From the Edit Column Modal
    if (!currentTableConfig || !editingColumn) return;
    const updatedColumns = columns.map(col => 
      col.name === editingColumn.name ? { ...col, ...updatedColumnData } : col // Use editingColumn.name for matching
    );
    updateCurrentTableConfigInState(updatedColumns);
    setSqlCommand('');
    setIsEditColumnModalOpen(false);
    setEditingColumn(null);
    setFeedbackMessage({ type: 'info', text: `Column '${updatedColumnData.name}' staged for update. Save configuration to persist.` });
  };

  const handleDeleteColumn = (columnName) => {
    if (!currentTableConfig) return;
    const updatedColumns = columns.filter(col => col.name !== columnName);
    updateCurrentTableConfigInState(updatedColumns);
    generateDeleteColumnSql(columnName);
    setFeedbackMessage({ type: 'info', text: `Column '${columnName}' staged for deletion. Save configuration to persist.` });
  };

  const handleSaveConfig = async () => {
    setIsSaving(true);
    setFeedbackMessage({ type: 'info', text: 'Saving configuration...' });
    try {
      await apiFetch('/api/config/columns', { 
        method: 'POST', 
        body: JSON.stringify(allTableConfigs),
        headers: { 'Content-Type': 'application/json' }
      });
      setFeedbackMessage({ type: 'success', text: 'Configuration saved successfully to backend!' });
    } catch (error) {
      console.error("Failed to save config:", error);
      setFeedbackMessage({ type: 'error', text: `Failed to save configuration: ${error.message}` });
    }
    setIsSaving(false);
  };

  const generateAddColumnSql = (column) => {
    if (!tableName) return;
    let sql = `ALTER TABLE ${tableName} ADD COLUMN ${column.name} `;
    switch (column.type) {
      case 'date': sql += 'DATE'; break;
      case 'decimal4': sql += 'DECIMAL(10, 4)'; break;
      case 'comma': sql += 'VARCHAR(255)'; break;
      case 'datetime': sql += 'DATETIME'; break;
      case 'int': sql += 'INT'; break;
      case 'float': sql += 'FLOAT'; break;
      case 'boolean': sql += 'BIT'; break;
      default: sql += 'VARCHAR(255)';
    }
    sql += ';';
    setSqlCommand(sql);
  };

  const generateDeleteColumnSql = (columnName) => {
    if (!tableName) return;
    const sql = `ALTER TABLE ${tableName} DROP COLUMN ${columnName};`;
    setSqlCommand(sql);
  };

  const generateCreateTableSql = (newTable) => {
    let sql = `CREATE TABLE ${newTable.tableName} (\n`;
    if (newTable.columns && newTable.columns.length > 0) {
      sql += newTable.columns.map((col, idx) => {
        let colDef = `  ${col.name} `;
        switch (col.type) {
          case 'date': colDef += 'DATE'; break;
          case 'decimal4': colDef += 'DECIMAL(10, 4)'; break;
          case 'comma': colDef += 'VARCHAR(255)'; break;
          case 'datetime': colDef += 'DATETIME'; break;
          case 'int': colDef += 'INT'; break;
          case 'float': colDef += 'FLOAT'; break;
          case 'boolean': colDef += 'BIT'; break;
          default: colDef += 'VARCHAR(255)';
        }
        if (col.isPrimaryKey) colDef += ' PRIMARY KEY';
        return colDef;
      }).join(',\n');
    } else {
        sql += `  ${newTable.tableName}_ID VARCHAR(255) PRIMARY KEY`;
    }
    sql += '\n);';
    setSqlCommand(sql);
  };

  const startEditColumn = (columnToEdit) => {
    setEditingColumn(columnToEdit);
    setIsEditColumnModalOpen(true);
    setSqlCommand('');
    setFeedbackMessage({ type: '', text: '' });
  };

  const handleTableChange = (event) => {
    const newIndex = parseInt(event.target.value, 10);
    if (isNaN(newIndex) || newIndex < 0 || newIndex >= allTableConfigs.length) {
        setSelectedTableIndex(null);
    } else {
        setSelectedTableIndex(newIndex);
    }
    setEditingColumn(null); // Clear any column being edited
    setIsEditColumnModalOpen(false); // Close edit modal if open
    setSqlCommand('');
    setFeedbackMessage({ type: '', text: '' });
  };

  const handleAddNewTable = () => {
    const trimmedNewTableName = newTableNameInput.trim();
    console.log('handleAddNewTable called with:', trimmedNewTableName);
    
    if (!trimmedNewTableName) {
      setFeedbackMessage({ type: 'error', text: 'Please enter a name for the new table.' });
      return;
    }
    const existingTable = allTableConfigs.find(t => t.tableName === trimmedNewTableName);
    if (existingTable) {
      setFeedbackMessage({ type: 'error', text: 'A table with this name already exists.' });
      return;
    }
    const newTable = {
      tableName: trimmedNewTableName,
      displayName: trimmedNewTableName, // Default display name same as table name
      columns: [],
      tableCSS: tableCssTemplates.default.css, // Default table CSS
      titleCSS: titleCssTemplates.default.css, // Default title CSS
      aiInstructions: '' // Default empty AI instructions
    };
    const updatedConfigs = [...allTableConfigs, newTable];
    console.log('Before update - allTableConfigs length:', allTableConfigs.length);
    console.log('After update - updatedConfigs length:', updatedConfigs.length);
    console.log('New table:', newTable);
    console.log('Setting selectedTableIndex to:', updatedConfigs.length - 1);
    
    setAllTableConfigs(updatedConfigs);
    setSelectedTableIndex(updatedConfigs.length - 1);
    setNewTableNameInput('');
    generateCreateTableSql(newTable);
    setFeedbackMessage({ type: 'info', text: `Table ${trimmedNewTableName} staged for addition. Save configuration to persist.` });
  };

  const handleUnconfigureTable = () => {
    if (selectedTableIndex === null || !currentTableConfig) {
      setFeedbackMessage({ type: 'error', text: 'Please select a table to unconfigure.' });
      return;
    }

    const tableToRemove = currentTableConfig.tableName;
    
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to unconfigure table "${tableToRemove}"?\n\n` +
      `This will:\n` +
      `• Remove the table configuration from the system\n` +
      `• Remove all ${columns.length} column configurations\n` +
      `• Remove styling and AI instructions\n\n` +
      `Note: This does NOT delete the actual database table, only removes it from the configuration.\n\n` +
      `Click OK to proceed or Cancel to abort.`
    );

    if (!confirmed) {
      return;
    }

    console.log('handleUnconfigureTable called for:', tableToRemove);
    
    // Create new array without the selected table
    const updatedConfigs = allTableConfigs.filter((_, index) => index !== selectedTableIndex);
    
    // Update state
    setAllTableConfigs(updatedConfigs);
    
    // Adjust selectedTableIndex
    if (updatedConfigs.length === 0) {
      // No tables left
      setSelectedTableIndex(null);
    } else if (selectedTableIndex >= updatedConfigs.length) {
      // Selected index is now out of bounds, select the last table
      setSelectedTableIndex(updatedConfigs.length - 1);
    } else {
      // Keep the same index if possible, which will now point to a different table
      setSelectedTableIndex(selectedTableIndex);
    }
    
    // Clear any editing state
    setEditingColumn(null);
    setIsEditColumnModalOpen(false);
    
    // Generate SQL command for dropping the table
    generateDropTableSql(tableToRemove);
    
    // Provide feedback
    setFeedbackMessage({ 
      type: 'success', 
      text: `Table "${tableToRemove}" has been unconfigured and removed from the system. ${updatedConfigs.length} table(s) remaining. Save configuration to persist changes.` 
    });
  };

  const generateDropTableSql = (tableName) => {
    const sql = `-- WARNING: This will permanently delete the table and all its data!\n-- DROP TABLE ${tableName};`;
    setSqlCommand(sql);
  };

  // MODIFIED: Functions for DB Column Discovery Modal
  const handleOpenDiscoverModal = async () => {
    if (!tableName) {
      setFeedbackMessage({ type: 'error', text: 'Please select a table configuration first.' });
      return;
    }
    
    console.log('🔍 Starting column discovery for table:', tableName);
    console.log('📋 Using schema:', dbSchemaName);
    console.log('🌐 API endpoint will be:', `/api/db/columnlist/${tableName}?dbSchema=${encodeURIComponent(dbSchemaName)}`);
    
    setIsDiscoverModalOpen(true);
    setIsLoadingDbColumns(true);
    setDiscoveredDbColumns([]);
    setSelectedDbColumnsToAdd(new Set());
    
    try {
      console.log('📡 Making API request...');
      const result = await apiFetch(`/api/db/columnlist/${tableName}?dbSchema=${encodeURIComponent(dbSchemaName)}`);
      
      console.log('📊 API response received:', result);
      console.log('📊 Response type:', typeof result);
      console.log('📊 Is array?', Array.isArray(result));
      
      if (Array.isArray(result)) {
        console.log('✅ Valid array response with', result.length, 'columns');
        if (result.length === 0) {
          setFeedbackMessage({ 
            type: 'warning', 
            text: `No columns found for table '${tableName}' in schema '${dbSchemaName}'. Please check:
            1. Table name is correct (case-sensitive)
            2. Schema name is correct (usually 'dbo')
            3. Database connection is working
            4. Table exists in the database` 
          });
        } else {
          console.log('📋 Columns found:', result.map(col => `${col.COLUMN_NAME} (${col.DATA_TYPE})`));
        }
        setDiscoveredDbColumns(result);
      } else {
        console.error("❌ Unexpected result from /api/db/columnlist:", result);
        setFeedbackMessage({ 
          type: 'error', 
          text: `Failed to discover columns: Invalid response from server. Expected array, got ${typeof result}. Response: ${JSON.stringify(result)}` 
        });
        setDiscoveredDbColumns([]);
      }
    } catch (error) {
      console.error("❌ Failed to fetch DB columns:", error);
      console.error("❌ Error details:", {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        stack: error.stack
      });
      
      let errorMessage = `Error discovering columns: ${error.message}`;
      
      // Provide more specific error messages based on common issues
      if (error.message.includes('fetch')) {
        errorMessage += '\n\n🔧 Possible solutions:\n• Check if backend server is running\n• Verify API endpoint URL is correct\n• Check network connectivity';
      } else if (error.message.includes('401') || error.message.includes('403')) {
        errorMessage += '\n\n🔐 Authentication issue:\n• Check if you are logged in\n• Verify admin permissions';
      } else if (error.message.includes('500')) {
        errorMessage += '\n\n🗄️ Database issue:\n• Check database connection\n• Verify table exists\n• Check database permissions';
      }
      
      setFeedbackMessage({ type: 'error', text: errorMessage });
      setDiscoveredDbColumns([]);
    }
    setIsLoadingDbColumns(false);
  };

  const handleCloseDiscoverModal = () => {
    setIsDiscoverModalOpen(false);
  };

  const handleDbColumnSelectionToggle = (columnName) => {
    const newSelection = new Set(selectedDbColumnsToAdd);
    if (newSelection.has(columnName)) {
      newSelection.delete(columnName);
    } else {
      newSelection.add(columnName);
    }
    setSelectedDbColumnsToAdd(newSelection);
  };

  const handleAddSelectedDbColumnsToConfig = () => {
    if (!currentTableConfig) return;
    const currentConfigColNames = new Set(columns.map(c => c.name));
    let newColumnsAddedCount = 0;
    const newConfigColumns = [...columns];

    Array.from(selectedDbColumnsToAdd).forEach(dbColName => {
      if (!currentConfigColNames.has(dbColName)) {
        const dbColDetails = discoveredDbColumns.find(col => col.COLUMN_NAME === dbColName);
        const newOrder = newConfigColumns.length > 0 ? Math.max(...newConfigColumns.map(c => c.order), 0) + 1 : 1;
        
        let defaultType = 'string';
        let defaultUnit = '';

        if (dbColDetails && dbColDetails.DATA_TYPE) {
            // Use shared function for consistent type mapping
            defaultType = mapDbTypeToColumnType(dbColDetails.DATA_TYPE);
        }

        // Determine format attributes based on type
        const formatType = mapDbTypeToColumnType(dbColDetails.DATA_TYPE);
        const columnConfig = {
          name: dbColName,
          label: generateLabelFromName(dbColName), // Use new label generation function
          type: defaultType,
          order: newOrder,
          unit: defaultUnit, // Use the determined default unit
          desc: `${dbColName} (${dbColDetails.DATA_TYPE})`,
          link: '',
          format: formatType
        };

        // Add format-specific attributes
        switch (formatType) {
          case 'text':
          case 'textarea':
            columnConfig.textFormat = 'normal';
            break;
          case 'number':
            // Context-aware number formatting
            if (dbColName.toLowerCase().includes('depth') || dbColName.toLowerCase().includes('td')) {
              columnConfig.numberFormat = '0,000';
            } else if (dbColName.toLowerCase().includes('latitude') || dbColName.toLowerCase().includes('longitude')) {
              columnConfig.numberFormat = '0.0000';
            } else if (dbColName.toLowerCase().includes('id') && dbColDetails.DATA_TYPE.toLowerCase().includes('int')) {
              columnConfig.numberFormat = '0';
            } else if (dbColDetails.DATA_TYPE.toLowerCase().includes('int')) {
              columnConfig.numberFormat = '0';
            } else {
              columnConfig.numberFormat = '0.00';
            }
            break;
          case 'currency':
            columnConfig.currencyFormat = '$0,000.00';
            break;
          case 'date':
            columnConfig.dateFormat = 'DD/MM/YYYY';
            break;
          case 'time':
            columnConfig.timeFormat = dbColDetails.DATA_TYPE.toLowerCase().includes('datetime') ? 'DD/MM/YYYY HH:mm' : 'HH:mm';
            break;
        }

        newConfigColumns.push(columnConfig);
        newColumnsAddedCount++;
      }
    });

    if (newColumnsAddedCount > 0) {
      updateCurrentTableConfigInState(newConfigColumns.sort((a, b) => a.order - b.order));
      setFeedbackMessage({ type: 'success', text: `${newColumnsAddedCount} new column(s) added/staged from DB. Save configuration to persist.` });
    } else if (selectedDbColumnsToAdd.size > 0) {
      setFeedbackMessage({ type: 'info', text: 'Selected columns are already in the configuration.' });
    } else {
      setFeedbackMessage({ type: 'info', text: 'No new columns were selected to add.'});
    }
    setSelectedDbColumnsToAdd(new Set());
    setIsDiscoverModalOpen(false);
  };

  // Drag and drop handlers
  const handleDragStart = (e, column, index) => {
    setDraggedColumn({ column, index });
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
    e.currentTarget.style.opacity = '0.5';
    
    // Add a custom drag image for better visual feedback
    const dragImage = e.currentTarget.cloneNode(true);
    dragImage.style.transform = 'rotate(5deg)';
    dragImage.style.opacity = '0.8';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 50, 20);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedColumn(null);
    setDragOverIndex(null);
    setIsDragging(false);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (!draggedColumn || draggedColumn.index === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    const newColumns = [...columns];
    const sortedColumns = newColumns.sort((a, b) => a.order - b.order);
    
    // Remove the dragged column from its current position
    const draggedItem = sortedColumns.splice(draggedColumn.index, 1)[0];
    
    // Insert it at the new position
    sortedColumns.splice(dropIndex, 0, draggedItem);
    
    // Update the order values based on new positions
    const reorderedColumns = sortedColumns.map((col, index) => ({
      ...col,
      order: index + 1
    }));

    updateCurrentTableConfigInState(reorderedColumns);
    setFeedbackMessage({ 
      type: 'success', 
      text: `Column "${draggedItem.label || draggedItem.name}" moved. Save configuration to persist changes.` 
    });
    
    setDraggedColumn(null);
    setDragOverIndex(null);
  };

  // Test database connection function
  const testDatabaseConnection = async () => {
    console.log('🧪 Testing database connection...');
    setFeedbackMessage({ type: 'info', text: 'Testing database connection...' });
    
    try {
      // Test with a simple table that should exist
      const testResult = await apiFetch('/api/db/columnlist/Well?dbSchema=dbo');
      console.log('✅ Database connection test successful:', testResult);
      
      if (Array.isArray(testResult) && testResult.length > 0) {
        setFeedbackMessage({ 
          type: 'success', 
          text: `✅ Database connection working! Found ${testResult.length} columns in 'Well' table. You can now try discovering columns for DailyWellData.` 
        });
      } else {
        setFeedbackMessage({ 
          type: 'warning', 
          text: '⚠️ Database connected but no columns found in Well table. Check your database schema.' 
        });
      }
    } catch (error) {
      console.error('❌ Database connection test failed:', error);
      setFeedbackMessage({ 
        type: 'error', 
        text: `❌ Database connection test failed: ${error.message}. Please check backend server and database connection.` 
      });
    }
  };

  // NEW: Fetch all available tables from database
  const fetchAvailableDbTables = async () => {
    console.log('📋 Fetching available database tables...');
    setIsLoadingDbTables(true);
    setFeedbackMessage({ type: 'info', text: 'Loading available database tables...' });
    
    try {
      const result = await apiFetch(`/api/db/tables?dbSchema=${encodeURIComponent(dbSchemaName)}`);
      console.log('📊 Available tables:', result);
      
      if (Array.isArray(result)) {
        setAvailableDbTables(result);
        
        // Analyze which tables are new vs configured
        const configuredTableNames = new Set(allTableConfigs.map(config => config.tableName));
        const newTables = result.filter(dbTable => !configuredTableNames.has(dbTable.TABLE_NAME));
        const missingTables = allTableConfigs.filter(config => 
          !result.some(dbTable => dbTable.TABLE_NAME === config.tableName)
        );
        
        let message = `✅ Found ${result.length} tables in database.`;
        if (newTables.length > 0) {
          message += ` ${newTables.length} new tables available to configure.`;
        }
        if (missingTables.length > 0) {
          message += ` ⚠️ ${missingTables.length} configured tables not found in database.`;
        }
        
        setFeedbackMessage({ type: 'success', text: message });
      } else {
        setFeedbackMessage({ type: 'error', text: 'Invalid response when fetching database tables.' });
      }
    } catch (error) {
      console.error('❌ Failed to fetch database tables:', error);
      setFeedbackMessage({ 
        type: 'error', 
        text: `Failed to fetch database tables: ${error.message}` 
      });
    }
    setIsLoadingDbTables(false);
  };

  // NEW: Validate column configuration against database
  const validateColumnConfiguration = async (tableName = null) => {
    const tableToValidate = tableName || currentTableConfig?.tableName;
    if (!tableToValidate) {
      setFeedbackMessage({ type: 'error', text: 'Please select a table to validate.' });
      return;
    }
    
    console.log('🔍 Validating column configuration for:', tableToValidate);
    setFeedbackMessage({ type: 'info', text: `Validating ${tableToValidate} configuration...` });
    
    try {
      const configColumns = tableName ? 
        allTableConfigs.find(config => config.tableName === tableName)?.columns || [] :
        columns;
        
      const validationUrl = `/api/db/validate-columns/${tableToValidate}?dbSchema=${encodeURIComponent(dbSchemaName)}&configColumns=${encodeURIComponent(JSON.stringify(configColumns))}`;
      const validation = await apiFetch(validationUrl);
      
      console.log('📊 Validation results:', validation);
      
      // Store validation results
      setColumnValidationResults(prev => ({
        ...prev,
        [tableToValidate]: validation
      }));
      
      // Generate feedback message
      const { summary } = validation;
      let message = `📊 Validation complete for ${tableToValidate}:\n`;
      message += `• Database: ${summary.totalDbColumns} columns\n`;
      message += `• Configuration: ${summary.totalConfigColumns} columns\n`;
      
      if (summary.missingInDbCount > 0) {
        message += `• ⚠️ ${summary.missingInDbCount} config columns not found in database\n`;
      }
      if (summary.missingInConfigCount > 0) {
        message += `• ℹ️ ${summary.missingInConfigCount} database columns not in configuration\n`;
      }
      if (summary.missingInDbCount === 0 && summary.missingInConfigCount === 0) {
        message += `• ✅ Perfect match!`;
      }
      
      const messageType = summary.missingInDbCount > 0 ? 'warning' : 'success';
      setFeedbackMessage({ type: messageType, text: message });
      
      setShowValidationPanel(true);
      
    } catch (error) {
      console.error('❌ Failed to validate column configuration:', error);
      setFeedbackMessage({ 
        type: 'error', 
        text: `Failed to validate configuration: ${error.message}` 
      });
    }
  };

  // NEW: Add a new table from database to configuration
  const addTableFromDatabase = async (dbTable) => {
    console.log('➕ Adding table from database:', dbTable);
    
    try {
      // Fetch columns for this table
      const columns = await apiFetch(`/api/db/columnlist/${dbTable.TABLE_NAME}?dbSchema=${encodeURIComponent(dbSchemaName)}`);
      
      if (Array.isArray(columns) && columns.length > 0) {
        // Create new table configuration
        const newTableConfig = {
          tableName: dbTable.TABLE_NAME,
          displayName: dbTable.TABLE_NAME,
          aiInstructions: `Auto-generated table configuration for ${dbTable.TABLE_NAME}. This table contains ${columns.length} columns from the database schema.`,
          columns: columns.map((col, index) => {
            const columnType = mapDbTypeToColumnType(col.DATA_TYPE);
            const formatType = mapColumnTypeToFormatType(columnType);
            
            const columnConfig = {
              name: col.COLUMN_NAME,
              label: generateLabelFromName(col.COLUMN_NAME),
              type: columnType,
              order: index + 1,
              unit: '',
              desc: `Auto-generated from database column ${col.COLUMN_NAME} (${col.DATA_TYPE})`,
              link: '',
              format: formatType
            };

            // Add format-specific attributes
            switch (formatType) {
              case 'text':
              case 'textarea':
                columnConfig.textFormat = 'normal';
                break;
              case 'number':
                // Context-aware number formatting
                if (col.COLUMN_NAME.toLowerCase().includes('depth') || col.COLUMN_NAME.toLowerCase().includes('td')) {
                  columnConfig.numberFormat = '0,000';
                } else if (col.COLUMN_NAME.toLowerCase().includes('latitude') || col.COLUMN_NAME.toLowerCase().includes('longitude')) {
                  columnConfig.numberFormat = '0.0000';
                } else if (col.COLUMN_NAME.toLowerCase().includes('id') && col.DATA_TYPE.toLowerCase().includes('int')) {
                  columnConfig.numberFormat = '0';
                } else if (col.DATA_TYPE.toLowerCase().includes('int')) {
                  columnConfig.numberFormat = '0';
                } else {
                  columnConfig.numberFormat = '0.00';
                }
                break;
              case 'currency':
                columnConfig.currencyFormat = '$0,000.00';
                break;
              case 'date':
                columnConfig.dateFormat = 'DD/MM/YYYY';
                break;
              case 'time':
                columnConfig.timeFormat = col.DATA_TYPE.toLowerCase().includes('datetime') ? 'DD/MM/YYYY HH:mm' : 'HH:mm';
                break;
            }

            return columnConfig;
          }),
          tableCSS: tableCssTemplates.default.css,
          titleCSS: titleCssTemplates.default.css,
          aiInstructions: `Auto-generated table configuration for ${dbTable.TABLE_NAME}. This table contains ${columns.length} columns from the database schema.`
        };
        
        const updatedConfigs = [...allTableConfigs, newTableConfig];
        setAllTableConfigs(updatedConfigs);
        setSelectedTableIndex(updatedConfigs.length - 1);
        
        setFeedbackMessage({ 
          type: 'success', 
          text: `✅ Added table '${dbTable.TABLE_NAME}' with ${columns.length} columns from database. Save configuration to persist.` 
        });
      } else {
        setFeedbackMessage({ 
          type: 'error', 
          text: `Failed to fetch columns for table '${dbTable.TABLE_NAME}'.` 
        });
      }
    } catch (error) {
      console.error('❌ Failed to add table from database:', error);
      setFeedbackMessage({ 
        type: 'error', 
        text: `Failed to add table: ${error.message}` 
      });
    }
  };

  // Use shared function for mapping database types to configuration types

  // NEW: Validate all table configurations
  const validateAllTableConfigurations = async () => {
    if (allTableConfigs.length === 0) {
      setFeedbackMessage({ type: 'warning', text: 'No table configurations to validate.' });
      return;
    }
    
    console.log('🔍 Validating all table configurations...');
    setFeedbackMessage({ type: 'info', text: `Validating ${allTableConfigs.length} table configurations...` });
    
    const validationPromises = allTableConfigs.map(async (config) => {
      try {
        const validationUrl = `/api/db/validate-columns/${config.tableName}?dbSchema=${encodeURIComponent(dbSchemaName)}&configColumns=${encodeURIComponent(JSON.stringify(config.columns || []))}`;
        const validation = await apiFetch(validationUrl);
        return { tableName: config.tableName, validation, success: true };
      } catch (error) {
        console.error(`❌ Failed to validate ${config.tableName}:`, error);
        return { tableName: config.tableName, error: error.message, success: false };
      }
    });
    
    try {
      const results = await Promise.all(validationPromises);
      
      // Process results
      const validationResults = {};
      let totalIssues = 0;
      let tablesWithIssues = 0;
      
      results.forEach(result => {
        if (result.success) {
          validationResults[result.tableName] = result.validation;
          const issues = result.validation.summary.missingInDbCount;
          if (issues > 0 || !result.validation.tableExists) {
            tablesWithIssues++;
            totalIssues += issues;
          }
        } else {
          // Handle validation errors
          validationResults[result.tableName] = {
            tableExists: false,
            error: result.error,
            summary: { missingInDbCount: 1, missingInConfigCount: 0, totalDbColumns: 0, totalConfigColumns: 0 }
          };
          tablesWithIssues++;
          totalIssues++;
        }
      });
      
      setColumnValidationResults(validationResults);
      setShowValidationPanel(true);
      
      // Generate summary message
      let message = `📊 Validation complete for ${allTableConfigs.length} tables:\n`;
      if (totalIssues === 0) {
        message += `✅ All configurations are synchronized with the database!`;
      } else {
        message += `⚠️ ${tablesWithIssues} tables have issues (${totalIssues} total problems)\n`;
        message += `Check the validation panel for details.`;
      }
      
      const messageType = totalIssues === 0 ? 'success' : 'warning';
      setFeedbackMessage({ type: messageType, text: message });
      
    } catch (error) {
      console.error('❌ Failed to validate all configurations:', error);
      setFeedbackMessage({ 
        type: 'error', 
        text: `Failed to validate configurations: ${error.message}` 
      });
    }
  };

  const createSampleLookupData = async () => {
    console.log('Creating sample lookup data...');
    setFeedbackMessage({ type: 'info', text: 'Creating sample lookup data...' });
    
    try {
      const response = await apiFetch('/api/db/create-sample-lookup-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response && response.message) {
        let message = `✅ ${response.message}\n\n`;
        message += `📋 Sample Tables Created:\n`;
        
        if (response.results) {
          response.results.forEach(result => {
            if (result.status === 'created') {
              message += `• ${result.table}: ${result.records} records\n`;
            } else if (result.status === 'error') {
              message += `• ${result.table}: Error - ${result.error}\n`;
            }
          });
        }
        
        if (response.usage) {
          message += `\n🔧 Usage Examples:\n`;
          Object.entries(response.usage).forEach(([table, config]) => {
            message += `• ${table}: ${config.description}\n`;
            message += `  - Table: ${config.lookupConfig.lookupTable}\n`;
            message += `  - Value: ${config.lookupConfig.lookupValueColumn}\n`;
            message += `  - Display: ${config.lookupConfig.lookupDisplayColumn}\n\n`;
          });
        }
        
        setFeedbackMessage({ type: 'success', text: message });
        
        // Refresh available tables after creating sample data
        fetchAvailableDbTables();
      } else {
        setFeedbackMessage({ type: 'error', text: 'Invalid response from sample data creation service.' });
      }
    } catch (error) {
      console.error('❌ Failed to create sample lookup data:', error);
      setFeedbackMessage({ 
        type: 'error', 
        text: `Failed to create sample lookup data: ${error.message}` 
      });
    }
  };

  if (userRole !== 'admin') {
    return <div style={{ padding: '20px' }}>Access Denied. You do not have permission to view this page.</div>;
  }

  if (isLoading) {
    return <div style={{ padding: '20px' }}>Loading configuration...</div>;
  }

  return (
    <div className="admin-config-manager">
      <div className="admin-config-header">
        <h1>Admin - Configuration Manager</h1>
      </div>
      
      {feedbackMessage.text && (
        <div className={`feedback-message ${feedbackMessage.type}`}>
          {feedbackMessage.text}
        </div>
      )}

      <div className="global-actions">
        <h2>Global Actions</h2>
        
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <button onClick={handleSaveConfig} disabled={isSaving} className="save-btn">
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </button>
          
          <button 
            onClick={() => setShowGlobalUnitsManager(true)} 
            className="discover-btn"
            style={{ backgroundColor: '#6f42c1', borderColor: '#6f42c1', color: 'white' }}
          >
            🌍 Global Units
          </button>
          
          <button 
            onClick={fetchAvailableDbTables} 
            disabled={isLoadingDbTables} 
            className="discover-btn"
            style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
          >
            {isLoadingDbTables ? 'Loading...' : '🔄 Sync with Database'}
          </button>
          
          <button 
            onClick={createSampleLookupData} 
            className="discover-btn"
            style={{ backgroundColor: '#fd7e14', borderColor: '#fd7e14', color: 'white' }}
          >
            🔗 Create Sample Lookup Data
          </button>
          
          <button 
            onClick={() => setShowLookupDemo(true)} 
            className="discover-btn"
            style={{ backgroundColor: '#17a2b8', borderColor: '#17a2b8', color: 'white' }}
          >
            🧪 Test Lookup Fields
          </button>
          
          {currentTableConfig && (
            <button 
              onClick={() => validateColumnConfiguration()} 
              className="discover-btn"
              style={{ backgroundColor: '#ffc107', borderColor: '#ffc107', color: '#212529' }}
            >
              🔍 Validate Current Table
            </button>
          )}
          
          {allTableConfigs.length > 0 && (
            <button 
              onClick={validateAllTableConfigurations} 
              className="discover-btn"
              style={{ backgroundColor: '#6f42c1', borderColor: '#6f42c1' }}
            >
              🔍 Validate All Tables
            </button>
          )}
        </div>
        
        <p className="form-help">
          Use "Sync with Database" to discover new tables and validate existing configurations against your actual database schema.
        </p>
      </div>

      <hr style={{ margin: '20px 0'}} />

      {/* Database Tables Discovery Section */}
      {availableDbTables.length > 0 && (
        <div className="database-tables-section" style={{ marginBottom: '24px' }}>
          <h2>🗄️ Database Tables Discovery</h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {/* Available Tables */}
            <div className="available-tables-panel" style={{ border: '1px solid #e9ecef', borderRadius: '8px', padding: '16px' }}>
              <h3>📋 Available Database Tables ({availableDbTables.length})</h3>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {availableDbTables.map(dbTable => {
                  const isConfigured = allTableConfigs.some(config => config.tableName === dbTable.TABLE_NAME);
                  return (
                    <div 
                      key={`${dbTable.TABLE_SCHEMA}.${dbTable.TABLE_NAME}`}
                      style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        padding: '8px 12px', 
                        margin: '4px 0',
                        backgroundColor: isConfigured ? '#d4edda' : '#fff3cd',
                        border: `1px solid ${isConfigured ? '#c3e6cb' : '#ffeaa7'}`,
                        borderRadius: '4px'
                      }}
                    >
                      <div>
                        <strong>{dbTable.TABLE_NAME}</strong>
                        <small style={{ display: 'block', color: '#6c757d' }}>
                          {dbTable.COLUMN_COUNT} columns • Schema: {dbTable.TABLE_SCHEMA}
                        </small>
                      </div>
                      <div>
                        {isConfigured ? (
                          <span style={{ color: '#155724', fontSize: '0.875rem' }}>✅ Configured</span>
                        ) : (
                          <button 
                            onClick={() => addTableFromDatabase(dbTable)}
                            style={{ 
                              padding: '4px 8px', 
                              fontSize: '0.75rem',
                              backgroundColor: '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            ➕ Add to Config
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Validation Results */}
            <div className="validation-panel" style={{ border: '1px solid #e9ecef', borderRadius: '8px', padding: '16px' }}>
              <h3>🔍 Configuration Validation</h3>
              {showValidationPanel && Object.keys(columnValidationResults).length > 0 ? (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {Object.entries(columnValidationResults).map(([tableName, validation]) => (
                    <div key={tableName} style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: '#495057' }}>{tableName}</h4>
                      
                      {!validation.tableExists && (
                        <div style={{ color: '#dc3545', marginBottom: '8px' }}>
                          ❌ Table does not exist in database
                        </div>
                      )}
                      
                      {validation.missingInDb.length > 0 && (
                        <div style={{ marginBottom: '8px' }}>
                          <strong style={{ color: '#dc3545' }}>⚠️ Config columns missing in DB:</strong>
                          <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                            {validation.missingInDb.map(col => (
                              <li key={col.name} style={{ color: '#dc3545', fontSize: '0.875rem' }}>
                                {col.name} ({col.type})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {validation.missingInConfig.length > 0 && (
                        <div style={{ marginBottom: '8px' }}>
                          <strong style={{ color: '#17a2b8' }}>ℹ️ DB columns not in config:</strong>
                          <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                            {validation.missingInConfig.slice(0, 5).map(col => (
                              <li key={col.COLUMN_NAME} style={{ color: '#17a2b8', fontSize: '0.875rem' }}>
                                {col.COLUMN_NAME} ({col.DATA_TYPE})
                              </li>
                            ))}
                            {validation.missingInConfig.length > 5 && (
                              <li style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                                ... and {validation.missingInConfig.length - 5} more
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                      
                      {validation.summary.missingInDbCount === 0 && validation.summary.missingInConfigCount === 0 && validation.tableExists && (
                        <div style={{ color: '#28a745' }}>
                          ✅ Perfect match! All columns synchronized.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                  Click "Validate Current Table" to see validation results here.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="table-selection-section">
        <label htmlFor="tableSelect">Select Table to Manage: </label>
        <select id="tableSelect" onChange={handleTableChange} value={selectedTableIndex === null ? '' : selectedTableIndex} className="table-select">
          <option value="" disabled>-- Select a Table --</option>
          {allTableConfigs.map((table, index) => {
            console.log(`Rendering option ${index}: ${table.tableName}`);
            return (
              <option key={index} value={index}>{table.tableName}</option>
            );
          })}
        </select>
        <div style={{ marginTop: '16px' }}>
          or <input 
            type="text" 
            value={newTableNameInput} 
            onChange={(e) => setNewTableNameInput(e.target.value)} 
            placeholder="Enter New Table Name"
            className="add-table-input"
          />
          <button onClick={handleAddNewTable} className="add-table-btn">Add New Table</button>
        </div>
        {currentTableConfig && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e9ecef' }}>
            <button 
              onClick={handleUnconfigureTable} 
              className="delete-btn"
              style={{ 
                backgroundColor: '#dc3545', 
                borderColor: '#dc3545', 
                color: 'white',
                padding: '8px 16px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              🗑️ Unconfigure Table "{currentTableConfig.tableName}"
            </button>
            <div className="form-help" style={{ marginTop: '8px', color: '#6c757d' }}>
              <strong>⚠️ Warning:</strong> This will remove the table configuration from the system but will NOT delete the actual database table.
            </div>
          </div>
        )}
        <div className="form-help" style={{ marginTop: '8px' }}>
          Debug: {allTableConfigs.length} tables total, selectedIndex: {selectedTableIndex}
        </div>
      </div>

      {currentTableConfig && (
        <div className="columns-panel">
          {/* Left Pane: Configured Columns List */}
          <div className="columns-list">
            <h3>
              Configured Columns for <span style={{ fontWeight: 'bold'}}>{tableName}</span>:
              {columns.length > 0 && (
                <span className="drag-help">
                  <br />
                  <small>💡 Drag and drop to reorder columns</small>
                </span>
              )}
            </h3>
            {columns.length > 0 ? (
              <div 
                className={`columns-container ${isDragging ? 'is-dragging' : ''}`}
                role="list"
                aria-label="Reorderable list of table columns"
              >
                {columns.sort((a,b) => a.order - b.order).map((col, index) => (
                  <div 
                    key={col.name} 
                    className={`column-item ${dragOverIndex === index ? 'drag-over' : ''} ${draggedColumn?.column.name === col.name ? 'dragging' : ''}`}
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, col, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    role="listitem"
                    aria-label={`Column ${col.label || col.name}, order ${col.order}. Drag to reorder.`}
                    tabIndex="0"
                  >
                    <div className="drag-handle" title="Drag to reorder">
                      ⋮⋮
                    </div>
                    <div className="column-content">
                      <div className="column-item-header">
                        <strong>{col.label || col.name}</strong> ({col.name}) - Order: {col.order}
                      </div>
                      <div className="column-item-details">
                        Type: {col.type || 'string'} | Unit: {col.unit || 'N/A'} <br />
                        Format: {col.format || 'N/A'} 
                        {col.format === 'number' && col.numberFormat && ` (${col.numberFormat})`}
                        {col.format === 'currency' && col.currencyFormat && ` (${col.currencyFormat})`}
                        {col.format === 'date' && col.dateFormat && ` (${col.dateFormat})`}
                        {col.format === 'time' && col.timeFormat && ` (${col.timeFormat})`} <br />
                        {col.options === 'lookup' && col.lookupTable && (
                          <>
                            <strong>🔗 Lookup:</strong> {col.lookupTable}.{col.lookupValueColumn} → {col.lookupDisplayColumn} <br />
                          </>
                        )}
                        Desc: {col.desc || 'N/A'} <br />
                        Link: {col.link || 'N/A'}
                      </div>
                      <div className="column-actions">
                        <button onClick={() => startEditColumn(col)} className="edit-btn">Edit</button>
                        <button onClick={() => handleDeleteColumn(col.name)} className="delete-btn">Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p>No columns defined for this table yet. Add one using the form to the right or discover from DB.</p>}
          </div>

          {/* Right Pane: Tools for adding columns */}
          <div className="tools-panel">
            <h2>Add or Discover Columns for <span style={{ fontWeight: 'bold'}}>{tableName}</span></h2>
            
            {/* Discover Columns Section */}
            <div className="discover-section">
              <h4>Discover Columns from Database</h4>
              <p className="form-help">Fetch column names directly from the database table '{tableName}'.</p>
              
              {/* Test Connection Button */}
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                <h5 style={{ margin: '0 0 8px 0', color: '#495057' }}>🧪 Database Connection Test</h5>
                <button 
                  onClick={testDatabaseConnection} 
                  className="discover-btn" 
                  style={{ backgroundColor: '#17a2b8', borderColor: '#17a2b8', marginRight: '8px' }}
                >
                  Test Database Connection
                </button>
                <small style={{ color: '#6c757d' }}>
                  Click this first to verify your database connection is working
                </small>
              </div>
              
              <div className="discover-controls">
                <label htmlFor="dbSchemaName">DB Schema:</label>
                <input 
                    type="text" 
                    id="dbSchemaName"
                    value={dbSchemaName} 
                    onChange={(e) => setDbSchemaName(e.target.value)} 
                    placeholder="dbo"
                    className="schema-input"
                />
                <button onClick={handleOpenDiscoverModal} className="discover-btn" disabled={isLoadingDbColumns}>
                  {isLoadingDbColumns ? 'Discovering...' : 'Discover & Add Columns'}
                </button>
              </div>
              <p className="form-help">
                  Uses backend endpoint <code>{`/api/db/columnlist/${tableName}?dbSchema=${dbSchemaName}`}</code>.
              </p>
            </div>
            
            {/* Add New Column Manually Section */}
            <div style={{marginTop: '20px'}}>
                <h4>Add New Column Manually</h4>
                <ColumnForm 
                    key={`add-form-${tableName}`} // Ensure form resets when table changes
                    onSubmit={handleAddColumn} 
                    initialData={{}} // Always empty for adding new
                    tableName={tableName}
                />
            </div>

            {/* Format Preview Section */}
            {columns.length > 0 && (
              <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                <h4>🎨 Format Configuration Preview</h4>
                <p style={{ color: '#6c757d', fontSize: '0.9em', marginBottom: '12px' }}>
                  Preview of how your columns will be formatted for display:
                </p>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {columns.filter(col => col.format).map((col, index) => (
                    <div key={col.name} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '8px 12px', 
                      margin: '4px 0',
                      backgroundColor: '#ffffff',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}>
                      <div>
                        <strong>{col.label || col.name}</strong>
                        <span style={{ color: '#6c757d', marginLeft: '8px' }}>
                          ({col.type})
                        </span>
                      </div>
                      <div style={{ 
                        padding: '4px 8px', 
                        backgroundColor: col.format === 'number' ? '#e3f2fd' : 
                                         col.format === 'currency' ? '#e8f5e8' : 
                                         col.format === 'date' ? '#fff3e0' : 
                                         col.format === 'time' ? '#fce4ec' : '#f5f5f5',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontFamily: 'monospace'
                      }}>
                        {col.format}
                        {col.format === 'number' && col.numberFormat && ` (${col.numberFormat})`}
                        {col.format === 'currency' && col.currencyFormat && ` (${col.currencyFormat})`}
                        {col.format === 'date' && col.dateFormat && ` (${col.dateFormat})`}
                        {col.format === 'time' && col.timeFormat && ` (${col.timeFormat})`}
                        {col.format === 'text' && col.textFormat && col.textFormat !== 'normal' && ` (${col.textFormat})`}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#d1ecf1', borderRadius: '4px', fontSize: '0.8em', color: '#0c5460' }}>
                  💡 <strong>Tip:</strong> Format configurations control how data is displayed in tables, exports, and reports. 
                  Use the edit button on any column above to modify its format settings.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!currentTableConfig && allTableConfigs.length > 0 && (
        <p>Select a table to manage its configuration or add a new table.</p>
      )}
      {!isLoading && !allTableConfigs.length && (
         <p>No tables configured yet. Add a new table to begin.</p>
      )}

      {/* CSS Configuration Section */}
      {currentTableConfig && (
        <div className="css-config-section">
          <h2>🎨 Table Styling & Display Configuration</h2>
          <p className="form-help">Configure how this table will be displayed, including custom CSS for styling and table title appearance.</p>
          
          <div className="css-config-grid">
            {/* Left Column: Table Display Settings */}
            <div className="css-config-panel">
              <h3>📋 Table Display Settings</h3>
              
              {/* Table Display Name */}
              <div className="form-group">
                <label>Table Display Name:</label>
                <input 
                  type="text" 
                  value={currentTableConfig.displayName || tableName} 
                  onChange={(e) => updateTableDisplayName(e.target.value)}
                  placeholder="Enter display name for this table"
                  className="form-input"
                />
                <div className="form-help">This name will be shown as the table title to users</div>
              </div>

              {/* AI Instructions Field */}
              <div className="form-group">
                <label>🤖 AI Extraction Instructions:</label>
                <textarea 
                  value={currentTableConfig.aiInstructions || ''} 
                  onChange={(e) => updateAIInstructions(e.target.value)}
                  placeholder="Enter special instructions for AI data extraction..."
                  className="form-textarea"
                  style={{ height: '120px' }}
                />
                <div className="form-help">
                  <strong>💡 AI Context Help:</strong> Provide specific instructions to help AI understand:
                  <ul style={{ margin: '4px 0', paddingLeft: '16px', fontSize: '0.875rem' }}>
                    <li>Data patterns or formats specific to this table</li>
                    <li>Field relationships and dependencies</li>
                    <li>Common data sources or document types</li>
                    <li>Special extraction rules or exceptions</li>
                    <li>Industry-specific terminology or abbreviations</li>
                  </ul>
                </div>
              </div>

              {/* Title CSS Templates */}
              <div className="form-group">
                <label>Title Style Template:</label>
                <select 
                  onChange={(e) => {
                    if (e.target.value) applyTitleCSSTemplate(e.target.value);
                  }}
                  className="form-select"
                >
                  <option value="">Select a title style...</option>
                  {Object.entries(titleCssTemplates).map(([key, template]) => (
                    <option key={key} value={key}>{template.name}</option>
                  ))}
                </select>
              </div>

              {/* Custom Title CSS */}
              <div className="form-group">
                <label>Custom Title CSS:</label>
                <textarea 
                  value={currentTableConfig.titleCSS || ''} 
                  onChange={(e) => updateTitleCSS(e.target.value)}
                  placeholder="Enter custom CSS for table title..."
                  className="form-textarea"
                  style={{ height: '100px' }}
                />
                <div className="form-help">CSS will be applied to .table-title class</div>
              </div>
            </div>

            {/* Right Column: Table CSS Settings */}
            <div className="css-config-panel">
              <h3>🎯 Table CSS Settings</h3>
              
              {/* Table CSS Templates */}
              <div className="form-group">
                <label>Table Style Template:</label>
                <select 
                  onChange={(e) => {
                    if (e.target.value) applyTableCSSTemplate(e.target.value);
                  }}
                  className="form-select"
                >
                  <option value="">Select a table style...</option>
                  {Object.entries(tableCssTemplates).map(([key, template]) => (
                    <option key={key} value={key}>{template.name}</option>
                  ))}
                </select>
                <div className="form-help">Choose from predefined table styles</div>
              </div>

              {/* Custom Table CSS */}
              <div className="form-group">
                <label>Custom Table CSS:</label>
                <textarea 
                  value={currentTableConfig.tableCSS || ''} 
                  onChange={(e) => updateTableCSS(e.target.value)}
                  placeholder="Enter custom CSS for table styling..."
                  className="form-textarea"
                  style={{ height: '200px' }}
                />
                <div className="form-help">CSS will be applied to table, th, td, tr elements</div>
              </div>

              {/* CSS Help */}
              <div className="css-help">
                <strong>💡 CSS Tips:</strong>
                <ul>
                  <li>Use <code>table</code> for overall table styling</li>
                  <li>Use <code>th</code> for header styling</li>
                  <li>Use <code>td</code> for cell styling</li>
                  <li>Use <code>tr:hover</code> for row hover effects</li>
                  <li>Use <code>.table-title</code> for title styling</li>
                </ul>
              </div>
            </div>
          </div>

          {/* AI Instructions Preview */}
          {currentTableConfig.aiInstructions && (
            <div className="ai-instructions-preview">
              <h4>🤖 AI Instructions Preview</h4>
              <div style={{ marginBottom: '10px' }}>
                <strong>Instructions that will be sent to AI for data extraction:</strong>
              </div>
              <div className="ai-instructions-preview-content">
                {currentTableConfig.aiInstructions}
              </div>
              <div className="form-help">
                These instructions will be included in AI prompts when extracting data for the "{currentTableConfig.displayName || tableName}" table
              </div>
            </div>
          )}

          {/* Preview Section */}
          {(currentTableConfig.tableCSS || currentTableConfig.titleCSS) && (
            <div className="css-preview">
              <h4>🔍 CSS Preview</h4>
              <div style={{ marginBottom: '10px' }}>
                <strong>Generated CSS for DataTable.js:</strong>
              </div>
              <div className="css-preview-code">
{(currentTableConfig.titleCSS || '') + '\n\n' + (currentTableConfig.tableCSS || '')}
              </div>
              <div className="form-help">
                This CSS will be applied when displaying the table using DataTable.js
              </div>
            </div>
          )}
        </div>
      )}

      {sqlCommand && (
        <div className="sql-preview">
          <h3>Suggested SQL Command:</h3>
          <pre>{sqlCommand}</pre>
        </div>
      )}

      <Modal isOpen={isDiscoverModalOpen} onClose={handleCloseDiscoverModal} title={`Discover Columns for Table: ${tableName}`} width="600px">
        {isLoadingDbColumns ? (
          <p>Loading database columns...</p>
        ) : discoveredDbColumns.length > 0 ? (
          <>
            <p>Select columns from table '{tableName}' (Schema: '{dbSchemaName}') to add to configuration:</p>
            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #eee', marginBottom: '10px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{borderBottom: '1px solid #ccc', padding: '5px', textAlign: 'left'}}>Add</th>
                    <th style={{borderBottom: '1px solid #ccc', padding: '5px', textAlign: 'left'}}>DB Column Name</th>
                    <th style={{borderBottom: '1px solid #ccc', padding: '5px', textAlign: 'left'}}>DB Data Type</th>
                  </tr>
                </thead>
                <tbody>
                  {discoveredDbColumns.map(col => (
                    <tr key={col.COLUMN_NAME}>
                      <td style={{borderBottom: '1px solid #eee', padding: '5px'}}>
                        <input 
                          type="checkbox" 
                          checked={selectedDbColumnsToAdd.has(col.COLUMN_NAME)}
                          onChange={() => handleDbColumnSelectionToggle(col.COLUMN_NAME)}
                          disabled={columns.some(c => c.name === col.COLUMN_NAME)}
                        />
                      </td>
                      <td style={{borderBottom: '1px solid #eee', padding: '5px'}}>{col.COLUMN_NAME}</td>
                      <td style={{borderBottom: '1px solid #eee', padding: '5px'}}>{col.DATA_TYPE}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={handleAddSelectedDbColumnsToConfig} style={{ padding: '8px 15px', marginRight: '10px' }} disabled={selectedDbColumnsToAdd.size === 0}>
                Add Selected ({selectedDbColumnsToAdd.size}) to Config
            </button>
            <button onClick={handleCloseDiscoverModal} style={{ padding: '8px 15px' }}>Close</button>
          </>
        ) : (
          <p>No columns found for table '{tableName}' with schema '{dbSchemaName}', or an error occurred. Check table/schema name and server logs.</p>
        )}
      </Modal>

      {editingColumn && (
          <Modal 
            isOpen={isEditColumnModalOpen} 
            onClose={() => { setIsEditColumnModalOpen(false); setEditingColumn(null); }} 
            title={`Edit Column: ${editingColumn.name} (for Table: ${tableName})`}
            width="500px"
          >
            <ColumnForm 
                key={`edit-form-${tableName}-${editingColumn.name}`} // Ensure form re-initializes correctly
                onSubmit={handleUpdateColumn} 
                initialData={editingColumn} 
                tableName={tableName}
            />
        </Modal>
      )}

      {/* Global Units Manager Modal */}
      {showGlobalUnitsManager && (
        <GlobalUnitsManager 
          onClose={() => setShowGlobalUnitsManager(false)} 
        />
      )}

      {/* Lookup Field Demo Modal */}
      {showLookupDemo && (
        <Modal 
          isOpen={showLookupDemo} 
          onClose={() => setShowLookupDemo(false)} 
          title="🔗 Lookup Field Demo & Testing"
          width="700px"
        >
          <LookupFieldDemo />
        </Modal>
      )}

    </div>
  );
};

export default AdminPage; 